import { CRUDMutations } from '@/utils/store-utilities'

/**
 * Default item factory for AI request log rows
 * @param {Object} item - Raw item data
 * @returns {Object} Normalized item
 */
const defaultItem = (item) => ({
  id: null,
  service: '',
  provider: '',
  user: null,
  session_id: '',
  ip_address: '',
  prompt_used: '',
  input_attachments: {},
  output_attachments: {},
  tokens: {
    input: 0,
    output: 0,
    total: 0,
  },
  cost: 0,
  status: 0,
  status_label: '',
  error_message: null,
  response_time_ms: 0,
  created_at: '',
  ...item,
})

export const state = () => ({
  items: [],
  item: null,
  services: [],
  stats: null,
})

export const mutations = {
  ...CRUDMutations(defaultItem),

  /**
   * Set the current item for detail view
   * @param {Object} state - Vuex state
   * @param {Object} item - The log item
   */
  setItem(state, item) {
    state.item = item
  },

  /**
   * Set available services list
   * @param {Object} state - Vuex state
   * @param {Array} services - List of service names
   */
  setServices(state, services) {
    state.services = services
  },

  /**
   * Set usage statistics
   * @param {Object} state - Vuex state
   * @param {Object} stats - Statistics data
   */
  setStats(state, stats) {
    state.stats = stats
  },
}

export const getters = {
  /**
   * Get all items
   * @param {Object} state - Vuex state
   * @returns {Array} List of items
   */
  list(state) {
    return state.items
  },

  /**
   * Get current item
   * @param {Object} state - Vuex state
   * @returns {Object|null} Current item
   */
  current(state) {
    return state.item
  },

  /**
   * Get available services
   * @param {Object} state - Vuex state
   * @returns {Array} List of services
   */
  getServices(state) {
    return state.services
  },

  /**
   * Get usage statistics
   * @param {Object} state - Vuex state
   * @returns {Object|null} Statistics
   */
  getStats(state) {
    return state.stats
  },
}

export const actions = {
  /**
   * Fetch a single log item by ID
   * @param {Object} context - Vuex context
   * @param {Object} params - { id, includePayload }
   * @returns {Promise} API response
   */
  async fetchOne({ commit }, { id, includePayload = false }) {
    const params = {}
    if (includePayload) {
      params.include_payload = 1
    }
    const response = await this.$axios.$get(`admin/aiRequestLogs/${id}`, { params })
    if (response && response.data) {
      commit('setItem', defaultItem(response.data))
    }
    return response
  },

  /**
   * Fetch available services
   * @param {Object} context - Vuex context
   * @returns {Promise} API response
   */
  async fetchServices({ commit }) {
    const response = await this.$axios.$get('admin/aiRequestLogs/services')
    if (response && response.data) {
      commit('setServices', response.data)
    }
    return response
  },

  /**
   * Fetch usage statistics
   * @param {Object} context - Vuex context
   * @param {Object} params - { service, from, to }
   * @returns {Promise} API response
   */
  async fetchStats({ commit }, params = {}) {
    const response = await this.$axios.$get('admin/aiRequestLogs/stats', { params })
    if (response) {
      commit('setStats', response)
    }
    return response
  },

  /**
   * Clear current item
   * @param {Object} context - Vuex context
   */
  clearItem({ commit }) {
    commit('setItem', null)
  },
}
